// 바이옴 팔레트 — **동(洞) 단위 색 가족**. districtPalette(theme_code) 위에 포개진다.
//
// 두 축이지 대체가 아니다
// ─────────────────────────────────────
//   바이옴(여기)      = 동 단위 · **색 가족**을 정한다 (벚꽃 분홍 / 침엽 청록 …)
//   디스트릭트(옆 파일) = 파티션 단위 · **그 가족 안의 명도·채도**를 정한다 (현행 DISTRICTS 표)
// 큰 붓으로 동을, 작은 붓으로 파티션을. 그래서 바이옴을 켜도 동 안이 통짜 단색이 안 된다.
//
// 왜 동 단위인가 — 실측(world_partition 12,266행, /tech lab-biome-blend §A)
//   전이 폭 45m 기준 전이대가 먹는 면적이 구 2.8% / **동 8.6%** / 그룹 31.2% / 파티션 71.8%.
//   파티션 단위로 나누면 땅의 72%가 전이대라 **순수한 바이옴이 존재하지 않는다**.
//   동은 한 변 1,027m(평균) — 걸어서 8~12분에 한 번 바뀌고 전이대를 한 번 지난다.
//
// 지금은 **하드 경계**다(동 폴리곤 안/밖). 거리가중 필드 + 선형 mix 는 L2~L4 단계 —
// /tech lab-biome-blend §C·§E 참조. 그전까지는 경계에 선이 보이는 게 정상이다.
//
// 체감 단위는 **구(區)**, 배정 단위는 **동** — 2층이다
// ─────────────────────────────────────────────────────────────
// 위 실측이 정한 "동 단위"는 **배정 해상도**지 체감 스케일이 아니다. 걷다가 분위기가
// 통째로 바뀌는 자리는 **구 경계**여야 한다(구는 11개뿐이라 그 자체를 바이옴 단위로
// 쓸 순 없다). 그래서 둘을 포갠다:
//   구 = 색 가족 **군(群)**  · 동 = 그 안의 변주(색상 ±15° · 채도 · 명도)
// 구 안을 걸으면 은은하게, 구 경계를 넘으면 가족이 통째로 바뀐다.
//
// [폐기된 조건] ~~맞닿는 두 바이옴의 tint 색상은 색상환 90° 이내~~
//   전이를 tint **값**으로 섞을 때는 조건이었다 — 90° 를 넘으면 보간 중간값이 회색으로
//   주저앉으니까. 그런데 전이가 **방향 벡터**로 옮겨 가면서(아래 색상 회전) 이유가 사라졌다:
//   셰이더가 채도의 **크기는 잔디 것으로 고정**하고 방향만 섞으므로, 두 방향이 마주 봐도
//   중간값은 회색이 아니라 「길이가 줄어 회전이 약해진 잔디색」이다. 회색 진창이 원리적으로 없다.
//   그래서 지금 용산(H150~160)과 중구(H40~45)는 색상환에서 110° 넘게 벌어져 있고, 그게 정상이다.
//
// [지금의 조건] 검사는 눈이 아니라 스크립트로: `node front/scripts/audit_biome_adjacency.mjs`
//   (인접 자료는 `python3 back/scripts/audit_dong_adjacency.py` — 전역 108개 동을 굽는다)
//   합격선은 두 줄이다 — ① 구 경계 이음매가 전부 ΔE 15 이상 ② 구 안 최대가 구 경계 최소보다 작다.
//
// [실측이 뒤집은 것] 초안은 "용산 초록 ↔ 중구 회백 사이에 서대문 톤을 완충으로 끼운다"
//   였는데, **서대문구는 용산구와 안 맞닿는다**(최소 597m). 게다가 용산↔중은 코너가 아니라
//   **후암동 북쪽 경계 전체**다(후암↔회현 696m · 후암↔필동 382m · 후암↔명동 143m).
//   완충은 구로 못 끼운다 — **동으로 끼워야 한다.** 그게 아래 namsan_edge·ridge_moss 다.

// ── 곱셈으로 안 되는 것을 여기서 푼다 ────────────────────────────────────────
// instanceColor 는 baseColor 에 **곱해진다**. 잔디 baseColor 는 (0.179, 0.458, 0.090) 로
// R·B 가 거의 0 이라 **뭘 곱해도 0 근처**다 — 분홍을 아무리 세게 곱해도 색상이 21° 밖에
// 안 돌고 그것도 올리브 쪽이다(/tech lab-district-biome §C 실측표).
// 그래서 곱셈 배율을 고르는 게 아니라 **목표 색을 정하고 거기로 가는 배율을 역산**한다.


// ── 바이옴 표 ────────────────────────────────────────────────────────────────
// ground : 바닥 컨트롤 틴트(128 중심 — 셰이더에서 col *= ctrl.rgb × 2.0) + 잔디 배합
//          mixG = A↔B 배합, mixC = ↔C(어두운 잔디) 배합. 값 감각은 DISTRICTS 표와 같다.
// foliage/accent/rock : 소품 서브메시가 **도달할 목표 색**. 명도는 서브메시 것을 쓰므로
//          여기서는 사실상 색상(H)·채도(S)만 쓰인다.
//
// [잎은 바닥 가족을 10° 안쪽에서 따라간다]
//   바닥이 색상 회전으로 H92 → H149(용산) 로 옮겨 갔는데 잎 hex 는 그대로여서 **43° 벌어졌다**.
//   화면에서는 「민트색 땅 위에 연두색 나무」로 읽혔다 — 두 가족이 같은 자리에 서 있는 꼴이다.
//   규칙: **잎(수관)은 그 바이옴 바닥 실렌더 색의 10° 노랑 쪽, 지면 식생은 5° 안쪽.**
//   방향이 노랑 쪽인 건 실제 수관이 잔디보다 따뜻하기 때문이고, 폭 10° 는 voxel_palette 의
//   HUE_TOL 14° 와 같은 감각이다(그 안이면 「같은 땅에서 자란 것」으로 읽힌다).
//
//   ⚠ 여기 적는 hex 는 **목표가 아니라 원점**이다 — biomeSubMul 이 STRENGTH(수관 0.9 ·
//   지면 0.7)만큼만 끌고 가므로, 실렌더를 원하는 자리에 세우려면 hex 를 그만큼 **넘겨서** 적는다.
//   그래서 understory hex 가 눈으로 보면 과하게 청록이다(#4c8a7a → 실렌더 H145). 판정은 hex 가
//   아니라 `node front/scripts/audit_biome_adjacency.mjs` 의 「잎 색」표로 한다.
//
//   ⚠ rock 은 색상을 안 쫓는다. 실렌더 채도가 0.07(거의 무채색)이라 색상각이 화면에 안 나오고,
//   STRENGTH 0.45 라 바닥에 맞추려면 목표를 190° 넘겨 적어야 한다 — 최단호가 반대로 돈다.
// ── 레이어 확장 — 「바이옴은 바닥색이 아니라 한 지역의 색 전부다」 ──
//
// [무엇이 없었나] 여태 바이옴이 가진 색은 넷뿐이었다 — ground(바닥) · foliage(수관) ·
//   understory(지면 식생) · rock(자갈). 그런데 화면의 절반은 그 넷이 아니다: 도로가 지나가고,
//   물이 있고, 절벽이 서 있고, 사람이 지은 것이 있다. 그것들이 전부 **전 지역 공통색**이라,
//   바이옴을 아무리 갈라도 「같은 회색 도로 · 같은 파란 물 · 같은 회색 건물」이 화면을 도로
//   한 색으로 묶었다. 바닥만 바뀌는 바이옴은 화면에서 「바닥만 바뀐 것」으로 읽힌다.
//
// [그래서 레이어를 넷 더 붙인다] road · water · cliff · structure. 각각 그 바이옴의
//   색 가족 **안에** 있되 서로 갈린다 — 마인크래프트가 같은 바이옴 안에서 잔디/흙/돌/물을
//   다른 색으로 두면서도 한 지역으로 읽히게 하는 것과 같은 구조다.
//
//   ⚠ **도로는 바닥을 따라가되 같은 색이 되면 안 된다**(SOP §3.5 의 정신은 유지).
//     「사람이 만든 것은 땅색을 안 따라간다」의 근거는 *구분이 사라지는 것*이었지 색 가족을
//     안 탄다는 게 아니다. 그래서 road 는 그 바이옴 ground 와 **명도·채도로 갈리되 색상은
//     같은 가족**에 둔다 — 사막의 길은 모래색 포장, 설원의 길은 얼어붙은 회청 포장.
//
// [등고선 밴드] bands = [저지, 중턱, 고지]. 셰이더가 표고로 이 셋을 섞는다. 여태 표고는
//   Y 압출에만 쓰이고 색에는 한 번도 안 쓰였다 — 그래서 「높은 데가 높아 보이는」 단서가
//   실루엣뿐이었다. 이게 마크의 「산 위는 돌, 더 위는 눈」과 같은 자리다.
//
// [tier] T1 = 색표만으로 성립 · T2 = 색 + 셰이더 한 줄(설원의 상향면 마스크 등) ·
//   T3 = 색으로는 성립 안 함, **공간**이라야 한다(동굴·실내). T3 는 지표 동에 배정하지 않는다
//   — 서울 지형 위에 보라를 칠하면 「심연」이 아니라 보라색 서울이 나온다(/tech 실측 결론).
//
// [air] 그 바이옴의 **공기** — 거리 안개의 색(hex)과 짙기(den 0~1).
//   바닥·잎만 갈리면 화면은 「발밑이 바뀐 것」으로 읽힌다. 분위기는 **먼 데**에서 나온다 —
//   같은 언덕이 분홍 아지랑이 너머와 회백 매연 너머에서 다른 곳으로 보인다.
//   den 은 안개 «거리»도 줄인다(짙을수록 시야가 짧다). 소비처는 `sky/AtmosphereController`.
//   ⚠ `temperate` 에는 **일부러 없다** — 기본값이 기준이라, 없으면 종전과 픽셀 하나 안 다르다.
//   ⚠ hex 는 **하늘색 위에 얹는 색**이지 안개색 자체가 아니다. 밤에는 절반만 끌어당기므로
//     낮 기준으로 고른다.
export const BIOMES = {
  // ── 기본 ──────────────────────────────────────────────────────────────────
  temperate: {
    name: '온대 활엽', tier: 'T1', sat: 0.7,
    desc: '서울의 기본값. 잔디 자기 색에서 거의 안 돌린다 — 다른 바이옴이 「특별해 보이려면」 기준이 되는 평범함이 있어야 한다.',
    ground: { tint: [130, 138, 124], mixG: 120, mixC: 80 },   // 기존 색상 방향을 유지하고 바닥 밝기만 올림
    foliage: '#78a957', understory: '#88ad59', rock: '#a29a86',
    road: '#b7a784', water: '#4f8fa4', cliff: '#a99f8d', structure: '#b0a18d',
    bands: ['#83ad58', '#9fa58e', '#c6bda8'],
  },
  sakura: {
    name: '벚꽃 분홍', tier: 'T1', sat: 0.75,
    desc: '천변·봄 골목. 바닥은 볕 드는 마른 흙이고 수관만 분홍이다 — 잔디까지 분홍이면 「벚꽃」이 아니라 「분홍 세상」이 된다.',
    air: { hex: '#f0d8e2', den: 0.28 },
    // ★ leafExempt — **이 바이옴은 잎이 바닥 가족을 일부러 벗어난다.** 감사의 「잎 20°」
    //   게이트는 「민트색 땅 위 연두색 나무」처럼 두 가족이 한자리에 선 사고를 막으려는 것인데,
    //   벚꽃은 그 어긋남 자체가 바이옴의 정의다 — 실제 벚꽃 풍경이 「흙빛 땅 + 초록 잔디 +
    //   분홍 수관」이다. 게이트를 통과시키려 hex 를 돌리면 벚꽃이 아니라 분홍 세상이 된다.
    //   ⚠ 이 플래그를 새로 달 땐 **왜 어긋나야 하는지**를 여기처럼 적는다. 안 적으면 다음
    //   사람에게는 「감사를 끄려고 붙인 것」과 구분이 안 된다.
    leafExempt: '벚꽃은 흙빛 땅 + 초록 잔디 위 분홍 수관이 정의다 — 잎이 바닥을 따라가면 분홍 세상이 된다',
    ground: { tint: [128, 140, 118], mixG: 172, mixC: 48 },   // 볕 드는 마른 흙 + A/B 잔디
    foliage: '#f095bb',      // 수관(leaf_*) — voxel_palette 의 leaf_cherry(0.78,0.52,0.63) 와 같은 가족
    understory: '#9cc25a',   // 지면 식생(grass/fern) — **분홍으로 안 간다**(아래 주석)
    rock: '#9a8f8c',
    road: '#b8a396', water: '#7f9fb5', cliff: '#9c8f8a', structure: '#ab9d96',
    bands: ['#93a355', '#9a8f8c', '#b5a8a4'],
  },
  conifer: {
    name: '침엽 청록', tier: 'T1', sat: 0.92,
    desc: '남산·관악 사면. 그늘진 산사면이라 어두운 잔디(C)를 최대로 섞고, 침엽은 초록에서 청록 쪽으로 40° 돈다.',
    air: { hex: '#93aaa6', den: 0.42 },
    ground: { tint: [116, 124, 121], mixG: 48, mixC: 165 },   // 화면 H150 S0.72 · 에메랄드(침엽) · C(어두운 잔디) 최대
    foliage: '#2f6b57',      // 침엽 — 초록에서 청록 쪽으로 +40°. 곱셈으로는 18°까지만 갔다
    understory: '#3e6b55',   // 그늘 밑 짙은 풀
    rock: '#6f7d7a',
    road: '#8a9490', water: '#3f6b73', cliff: '#6a7674', structure: '#7d8785',
    bands: ['#3e6b4e', '#6f7d7a', '#8f9895'],
  },

  // ── 계절·기후 (T1) ────────────────────────────────────────────────────────
  maple: {
    name: '단풍 황혼', tier: 'T1', sat: 0.95,
    // 바닥은 «낙엽 장»이다 — 잔디를 주홍으로 돌리면 0.6 지점이 올리브(H55)다. 난색 바이옴은 재질로만 간다.
    top: { flat: 'litter', slope: 'litter' },
    leafExempt: '낙엽 위 마른 풀은 누렇게 시든다 — 잎(주홍)과 바닥(낙엽)은 같은 난색 가족이고 풀만 벗어난다',
    desc: '가을 능선. 수관이 주홍으로 타고 바닥은 낙엽 깔린 흙빛. 벚꽃과 색상은 가까우나 채도·명도가 갈려 다른 계절로 읽힌다.',
    air: { hex: '#dcae86', den: 0.36 },
    ground: { tint: [143, 126, 114], mixG: 150, mixC: 70 },   // H25
    foliage: '#d97a3c', understory: '#a8894c', rock: '#948880',
    road: '#b09477', water: '#6f8fa0', cliff: '#9c8a76', structure: '#a89684',
    bands: ['#a8894c', '#948880', '#b0a496'],
  },
  volcanic: {
    name: '화산 현무암', tier: 'T2', sat: 0.6,
    desc: '검은 현무암 위로 용암 균열이 갈라져 빛난다(균열 발광 — biomeCrack). 공기는 주황 유황빛, 나무는 탄 고사목. 시안 volcanic_abc A.',
    air: { hex: '#d0703a', den: 0.42 },
    ground: { tint: [140, 96, 80], mixG: 165, mixC: 95 },
    foliage: '#352a26', understory: '#3e3029', rock: '#3a3230',
    road: '#7a5a48', water: '#ff7a2a', cliff: '#2e2624', structure: '#4a3c36',
    bands: ['#2c2624', '#3a3230', '#5a3a2c'],
  },
  snowfield: {
    name: '설원', tier: 'T2', sat: 0.54,
    desc: '250m 이상 고지·겨울. ★ 눈은 색이 아니라 방향이다 — 위를 보는 면만 하얗다. 상향면 마스크 없이 색만 밝히면 「밝은 초록」이 나온다.',
    air: { hex: '#dcefff', den: 0.32 },
    ground: { tint: [124, 140, 142], mixG: 100, mixC: 40 },   // 화면 H156 S0.50 · 청록(서리 민트)
    foliage: '#4f8f6e', understory: '#9fd0bf', rock: '#a8b0b5',
    road: '#9aa8b0', water: '#7d9db5', cliff: '#95a0a8', structure: '#aab2b8',
    bands: ['#a3b3b8', '#b5bfc4', '#d8dee2'],
  },
  arid: {
    name: '건조 황야', tier: 'T1', sat: 0.6,
    desc: '공사장·산업지·황무지. 흙이 마르고 갈라져 있다. 채도를 낮게 잡는 게 핵심 — 높으면 밀밭이 된다.',
    air: { hex: '#d5c9aa', den: 0.4 },
    ground: { tint: [143, 135, 117], mixG: 175, mixC: 55 },   // H42
    foliage: '#9c9464', understory: '#a89c6f', rock: '#a09880',
    road: '#b0a488', water: '#8a9384', cliff: '#a0947a', structure: '#aba08a',
    bands: ['#a89c6f', '#a09880', '#b8b09c'],
  },

  // ── 물·식생 (T1) ──────────────────────────────────────────────────────────
  waterside: {
    name: '수변 청록', tier: 'T1', sat: 0.75,
    desc: '한강·하천변. 습기 있는 초지와 갈대. 물이 주인공이라 water 를 가장 선명하게 둔다.',
    air: { hex: '#b8d4d8', den: 0.5 },
    ground: { tint: [112, 135, 135], mixG: 90, mixC: 95 },    // H180
    foliage: '#4f9c8a', understory: '#5fa896', rock: '#7d8f8f',
    road: '#8fa0a0', water: '#3f95a8', cliff: '#7a8a8a', structure: '#8a9898',
    bands: ['#5fa896', '#7d8f8f', '#9aa8a8'],
  },
  jungle: {
    name: '정글 심록', tier: 'T1', sat: 0.92,
    desc: '밀림·확장지. 이 카탈로그에서 가장 진한 초록. 어두운 잔디를 최대로 섞어 「빛이 바닥까지 안 온다」를 만든다.',
    air: { hex: '#93b298', den: 0.48 },
    ground: { tint: [94, 123, 106], mixG: 55, mixC: 175 },   // 화면 H136 S0.66 · 에메랄드
    foliage: '#2f7a3c', understory: '#3c7a48', rock: '#6f7d68',
    road: '#7d8a72', water: '#3f7a68', cliff: '#68755f', structure: '#78846e',
    bands: ['#3c7a48', '#6f7d68', '#8a9480'],
  },
  flowerfield: {
    name: '꽃동산', tier: 'T1', sat: 0.7,
    desc: '여의동·이벤트 구역. 꽃잎이 땅까지 깔려 지면 식생도 자홍을 탄다 — 다만 flower_* 자체는 안 건드린다(꽃은 종(種)의 색이지 땅의 색이 아니다).',
    air: { hex: '#e2c8de', den: 0.28 },
    ground: { tint: [145, 122, 140], mixG: 140, mixC: 60 },   // H315
    foliage: '#d675ae', understory: '#c47fae', rock: '#9a8f98',
    road: '#b09fa8', water: '#7f95b5', cliff: '#9c8f98', structure: '#a89ca4',
    bands: ['#93a862', '#9a8f98', '#b5a8b0'],
  },

  // ── 고지·황혼 ─────────────────────────────────────────────────────────────
  alpine: {
    name: '고산 암반', tier: 'T1', sat: 0.88,
    desc: '관악 정상부 602m. 식생선 위라 나무가 못 산다. 회청 암반이 바닥을 통째로 먹고, 설원과 화산 사이를 잇는 완충 바이옴이기도 하다.',
    air: { hex: '#cfe6f5', den: 0.35 },
    ground: { tint: [119, 139, 128], mixG: 110, mixC: 120 },   // 화면 H138 S0.60 · 에메랄드(고산 초원)
    foliage: '#3f8a60', understory: '#7fc0a0', rock: '#8f989f',
    road: '#95a0a8', water: '#6f8a9c', cliff: '#7d878f', structure: '#98a0a8',
    bands: ['#88949a', '#8f989f', '#b0b8bf'],
  },
  duskviolet: {
    name: '황혼 자보라', tier: 'T2', sat: 0.65,
    desc: 'event_pocket 심화. ★ 같은 색이라도 해 방향이 다르면 다른 구역으로 읽힌다 — 색만으로는 절반이고 대기(빛 각도)가 나머지 절반이다.',
    air: { hex: '#a894c6', den: 0.45 },
    // ★ leafExempt — **튜닝이 아니라 구조적 한계다.** 지면 식생은 잔디(H99)에서 STRENGTH 0.7
    //   만큼만 회전하므로 도달 가능한 최대가 H225(위로) 또는 H333(아래로)이다. 이 바이옴의
    //   바닥은 H278 — 정확히 그 사이 **도달 불가 구간**이고, 하필 잔디의 대척점(99+180=279)이라
    //   최단호 방향이 hex 한 자리에 뒤집힌다(실측: '#9660b8'→H224 / '#a355c4'→H335, 양쪽 다 54° 밖).
    //   즉 어느 hex 를 적어도 20° 게이트를 못 넘는다. 넘기려면 STRENGTH 를 올리거나(전역 영향)
    //   바닥 색상을 대척점에서 비켜 놓아야 한다 — 둘 다 이 바이옴 하나 때문에 하기엔 크다.
    //   T2 라 어차피 셰이더 작업(빛 각도)이 남아 있으니 그때 같이 본다.
    leafExempt: '바닥 H278 이 잔디 H99 의 대척점이라 지면 식생(STRENGTH 0.7)이 원리적으로 도달 못 한다 — T2 셰이더 작업 때 같이 푼다',
    ground: { tint: [122, 107, 128], mixG: 105, mixC: 110 },  // H285
    foliage: '#9a5cc4', understory: '#9660b8', rock: '#8a8095',
    road: '#9a8fa8', water: '#6f6f9c', cliff: '#7d7590', structure: '#948aa4',
    bands: ['#7f7395', '#8a8095', '#a89fb5'],
  },

  // ── 남산 일대 5종 — 용산구 3 + 중구 2 ─────────────────────────
  //
  // [초안의 「한 방향 사다리」는 폐기됐다] 처음엔 H 를 125→100→98→72→47 로 한 방향으로
  //   내려 「남쪽 초록에서 북쪽 석회로 서서히 빠진다」를 만들려 했다. 실측이 뒤집었다 —
  //   **미배정 동은 잔디 자기 색(≈H99)에 그대로 서 있다.** 회전을 안 타니까.
  //   그런데 사다리 중간 칸(slope H100 · edge H98)이 하필 거기였다. 결과: 용산 서쪽·동쪽
  //   구 경계(효창↔공덕 · 한남↔옥수 …)에서 **ΔE 2.7** — 걸어서 넘어도 아무 일도 안 일어난다.
  //
  // [그래서 두 무리로 갈랐다] 기준점이 잔디 초록 하나뿐이라 벌릴 방향은 둘밖에 없다 —
  //   청록 쪽이거나 노랑 쪽이거나. 둘 다 노랑으로 가면 서로 겹치므로 한쪽은 청록이어야 한다.
  //     용산 = 청록 쪽 H150~160 (남산 = 소나무 능선, 그늘진 숲)
  //     중구 = 노랑 쪽 H40~45  (도심 = 마른 석회 포장)
  //   구 안의 변주는 **5°씩**만 준다 — 그게 「구 경계에서 통째로, 구 안에서는 은은하게」다.
  //   실측(전역 108개 동 262 이음매): 구 경계 최소 ΔE 21.9 · 구 안 최대 7.0.
  //
  // [tint 숫자가 거의 안 변한 게 정상이다] 예: forest [99,124,101] → [99,124,116] (B 만 +15).
  //   tint 는 채도가 낮아 색상을 크게 돌려도 값이 조금 움직인다. 그런데 셰이더가 쓰는 건
  //   **정규화된 방향**이라, 그 조금이 회전축을 통째로 옮긴다. 밝기 느낌은 그대로 남는다.

  // ── 남산 일대 5종 — 용산구 3 + 중구 2 ─────────────────────────
  //
  // [초안의 「한 방향 사다리」는 폐기됐다] 처음엔 H 를 125→100→98→72→47 로 한 방향으로
  //   내려 「남쪽 초록에서 북쪽 석회로 서서히 빠진다」를 만들려 했다. 실측이 뒤집었다 —
  //   **미배정 동은 잔디 자기 색(≈H99)에 그대로 서 있다.** 회전을 안 타니까.
  //   그런데 사다리 중간 칸(slope H100 · edge H98)이 하필 거기였다. 결과: 용산 서쪽·동쪽
  //   구 경계(효창↔공덕 · 한남↔옥수 …)에서 **ΔE 2.7** — 걸어서 넘어도 아무 일도 안 일어난다.
  //
  // [그래서 두 무리로 갈랐다] 기준점이 잔디 초록 하나뿐이라 벌릴 방향은 둘밖에 없다 —
  //   청록 쪽이거나 노랑 쪽이거나. 둘 다 노랑으로 가면 서로 겹치므로 한쪽은 청록이어야 한다.
  //     용산 = 청록 쪽 H150~160 (남산 = 소나무 능선, 그늘진 숲)
  //     중구 = 노랑 쪽 H40~45  (도심 = 마른 석회 포장)
  //   구 안의 변주는 **5°씩**만 준다 — 그게 「구 경계에서 통째로, 구 안에서는 은은하게」다.
  //   실측(전역 108개 동 262 이음매): 구 경계 최소 ΔE 21.9 · 구 안 최대 7.0.
  //
  // [tint 숫자가 거의 안 변한 게 정상이다] 예: forest [99,124,101] → [99,124,116] (B 만 +15).
  //   tint 는 채도가 낮아 색상을 크게 돌려도 값이 조금 움직인다. 그런데 셰이더가 쓰는 건
  //   **정규화된 방향**이라, 그 조금이 회전축을 통째로 옮긴다. 밝기 느낌은 그대로 남는다.

  // ── 용산 «지역 테마» 8종 — 색이 아니라 «바닥 재질 + 공기 + 잎 가족»이 같이 갈린다 ─────
  //   `top` = 지형 상판 재질(terrainChunkGen.TERRAIN_PALETTE 이름). flat 평지 · slope 비탈(이끼 자리).
  //   재질이 있는 바이옴은 tint 가 화면 바닥색이 아니다 — 소품·원경·옆면이 그 방향으로만 돈다.
  windridge: {
    name: '바람 능선', tier: 'T1', sat: 0.82,
    desc: '해방촌·남산 동남 자락(용산2가동). 볕에 바랜 억새 능선 — 밀짚빛 풀, 얇고 맑은 공기. 후암 침엽 숲의 «반대편 사면».',
    air: { hex: '#e9eef2', den: 0.28 },
    // 볕 받은 황록 억새. 회색기보다 풀빛을 살리고 후암의 비취색과 색상으로 구분한다.
    ground: { tint: [154, 178, 98], mixG: 170, mixC: 30 },
    foliage: '#9cba65', understory: '#adc478', rock: '#a7a49c',
    road: '#b3a88e', water: '#6f95a8', cliff: '#9e9a90', structure: '#a89e90',
    bands: ['#b0ad7a', '#a7a49c', '#c2bfb4'],
  },
  sandbar: {
    name: '모래톱 강변', tier: 'T1', sat: 0.4,
    desc: '이촌(옛 한강 모래톱). 바닥이 통째로 모래다 — 밝은 난색 위에 물자국 띠. 버드나무는 초록으로 남긴다(모래 위 초록이 강변이다).',
    air: { hex: '#f1e4c8', den: 0.34 },
    top: { flat: 'sand', slope: 'sand' },
    leafExempt: '모래톱 위 버드나무는 초록이다 — 바닥(모래)과 잎이 갈려야 강변으로 읽힌다',
    ground: { tint: [190, 172, 134], mixG: 170, mixC: 40 },   // H38 모래
    foliage: '#7d9c58', understory: '#b8a878', rock: '#b3a18a',
    road: '#c2b394', water: '#5f93b0', cliff: '#b8a68e', structure: '#bfb09a',
    bands: ['#c9b48c', '#b3a18a', '#d8cbb2'],
  },
  icehouse: {
    name: '서빙고 서리', tier: 'T2', sat: 0.35,
    desc: '서빙고(西氷庫 — 조선의 얼음 창고). 바닥이 서리다: 푸른 흰색, 얼음이 비치는 웅덩이. 나무는 서리 가족(회청). 공기가 가장 밝고 차다.',
    air: { hex: '#e4eef8', den: 0.3 },
    top: { flat: 'frost', slope: 'frost' },
    leafExempt: '서리 위 침엽수는 바닥보다 초록 쪽 회청이다 — 눈밭의 나무가 눈색이면 실루엣이 사라진다',
    ground: { tint: [186, 200, 214], mixG: 110, mixC: 30 },   // H207 서리
    foliage: '#5f8f86', understory: '#b8d4e0', rock: '#aab6bf',
    road: '#b9c4cc', water: '#7da6c2', cliff: '#a4b0b8', structure: '#b4bec6',
    bands: ['#b7c8d2', '#aab6bf', '#dbe4ea'],
  },
  cinderyard: {
    name: '잿불 야드', tier: 'T2', sat: 0.35,
    desc: '한강로(용산역 정비창·철로). 바닥이 재다 — 어두운 난색 회색에 잉걸 점. 잎은 불씨빛(leaf_ember). 매캐한 공기. 올리브가 아니라 «검은 흙에 주황».',
    air: { hex: '#c9a48c', den: 0.3 },
    top: { flat: 'ash', slope: 'ash' },
    leafExempt: '재 위 마른 풀은 누렇게 시든다 — 잎(불씨빛)과 바닥(재)은 같은 난색 가족이고 풀만 벗어난다',
    ground: { tint: [96, 84, 78], mixG: 60, mixC: 120 },       // H18 재
    foliage: '#d0602a', understory: '#a0583a', rock: '#6e6460',
    road: '#7a6f68', water: '#5d7684', cliff: '#66605c', structure: '#7a716c',
    bands: ['#6c5f58', '#6e6460', '#8c827c'],
  },
  reedmarsh: {
    name: '갈대 늪', tier: 'T2', sat: 0.6,
    desc: '원효로2동 저지. 청회 진흙 바닥에 물이 고인 자리는 더 파랗다. 갈대는 마른 황갈(잎 가족 예외). 안개가 물처럼 깔린다.',
    air: { hex: '#b8dcd8', den: 0.32 },
    top: { flat: 'marsh', slope: 'marsh' },
    leafExempt: '늪의 갈대·버들은 황갈이다 — 바닥(청회 진흙)과 갈려야 늪으로 읽힌다',
    ground: { tint: [92, 110, 112], mixG: 60, mixC: 150 },     // H190 진흙
    foliage: '#7fbf4a', understory: '#9ccf6a', rock: '#7d8a8c',
    road: '#8e9a98', water: '#4f8494', cliff: '#758286', structure: '#87928f',
    bands: ['#5f7d7e', '#7d8a8c', '#9aa9a8'],
  },
  fungalhollow: {
    name: '버섯 골짜기', tier: 'T2', sat: 0.9,
    desc: '보광동 골짜기. 같은 늪흙 바닥인데 공기·잎이 «보라»다 — 버섯 갓빛. 갈대 늪(원효로2)과는 한강 건너편만큼 떨어져 있어 같은 장을 써도 안 겹친다.',
    air: { hex: '#c4b0e8', den: 0.35 },
    top: { flat: 'marsh', slope: 'marsh' },
    leafExempt: '버섯 골짜기의 잎은 포자빛 연보라다 — 바닥(청회 진흙)과 일부러 갈린다',
    ground: { tint: [140, 100, 170], mixG: 60, mixC: 140 },     // H266 보라 캐스트(소품·옆면)
    foliage: '#8f6fd0', understory: '#c08fe0', rock: '#6f6a7c',
    road: '#84808c', water: '#5a7a92', cliff: '#6c6876', structure: '#7e7888',
    bands: ['#6f6584', '#6f6a7c', '#9a92a6'],
  },
  worldtree: {
    name: '세계수 공원', tier: 'T1', sat: 0.9,
    desc: '효창·용문(효창공원). 금빛 도는 무성한 초록 — 큰 나무 아래 볕이 든다. 잔디를 황금 초록으로 돌리고 공기를 금빛으로. 재질은 안 바꾼다(공원은 잔디다).',
    air: { hex: '#f2e6c0', den: 0.30 },
    ground: { tint: [148, 160, 104], mixG: 170, mixC: 40 },   // H73 금빛 초록
    foliage: '#78a83a', understory: '#9ab850', rock: '#8f8b7e',
    road: '#b0a486', water: '#5e94a6', cliff: '#948e7e', structure: '#a49a86',
    bands: ['#9bb04e', '#8f8b7e', '#c2bc9a'],
  },
  feygrove: {
    name: '청파 비취 꽃숲', tier: 'T2', sat: 0.8,
    desc: '청파(靑坡 — 푸른 언덕). 비취 잔디 위에 분홍·연보라 꽃잎 융단(바닥 재질 petal). 잎은 비취, 덤불은 꽃빛. 후암 침엽 숲의 «밝은 쪽».',
    air: { hex: '#cfe6da', den: 0.36 },
    top: { flat: 'petal', slope: 'petal' },
    leafExempt: '꽃숲의 덤불은 꽃빛(분홍)이다 — 바닥의 꽃잎과 같은 가족, 잎(비취)과는 일부러 갈린다',
    ground: { tint: [96, 140, 110], mixG: 110, mixC: 70 },    // H139 비취
    foliage: '#4fa07a', understory: '#c78bb0', rock: '#8a9c92',
    road: '#a9a894', water: '#4f9aa0', cliff: '#8a9a92', structure: '#98a49a',
    bands: ['#5f9c7c', '#8a9c92', '#b9c9c0'],
  },

  namsan_forest: {
    name: '남산 능선 짙은 풀색', tier: 'T1', sat: 1.0,
    desc: '남산 본체(후암·용산2가) + 비취소 폭포 협곡. 자연 53~60% · 최고 228m. 이 월드에서 가장 진한 비취색이고, 중구 석회 가족과 맞붙는 쪽 끝이다.',
    air: { hex: '#a0c6b8', den: 0.38 },
    // 바닥 실렌더 H163(#319277) — 「초록」이 아니라 **비취**다. 잔디 A/B 를 초록 쪽으로
    // 끝까지 몰고(mixG 50) C(어두운 잔디)를 최대로 섞어(mixC 155) 명도를 눌러야 청록이 산다.
    // 바닥 실렌더 H145. 잔디 A/B 를 초록 쪽으로 몰고(mixG 50) C(어두운 잔디)를 최대로 섞어
    // 명도를 눌러야 「능선의 짙은 풀색」이 산다. 값을 흔들면 반드시
    // `node front/scripts/audit_biome_adjacency.mjs` 로 구 경계 ΔE 게이트를 다시 잰다.
    // 레퍼런스(이끼 낀 회색 협곡 + 청록 물)의 «맑은 청록». 어둡게 만들던 mixC 를 내리고 채도 방향을 또렷하게.
    ground: { tint: [82, 150, 118], mixG: 120, mixC: 80 },
    foliage: '#3fa25a',      // 바닥의 10° 노랑 쪽 — 수관은 늘 땅보다 따뜻하다
    understory: '#4aa872',   // 지면 식생은 5° 안쪽 — 풀은 땅에서 자란다
    rock: '#78897d',         // 이끼 앉은 화강암 (S0.07 이라 색상은 안 쫓는다 — 아래 주석)
    road: '#849784', water: '#46a3a8', cliff: '#72857f', structure: '#809282',
    bands: ['#43907f', '#78897d', '#94a89c'],
  },
  namsan_slope: {
    name: '남산 물가 밝은 풀색', tier: 'T1', sat: 1.0,
    desc: '한강 둔치와 배후 저지(남영·서빙고·한강로·이촌…). 볕이 들어 능선보다 밝고, 어두운 잔디를 뺀다.',
    air: { hex: '#b4d9c6', den: 0.34 },
    // 바닥 실렌더 H157(#379b75) — 능선(H163)보다 6° 노랑 쪽이고 더 밝다. 물가라 볕이 든다.
    // 바닥 실렌더 H130 — 능선(H145)보다 노랑 쪽이고 더 밝다. 물가라 볕이 든다.
    ground: { tint: [116, 140, 124], mixG: 70, mixC: 68 },
    foliage: '#4cb85e',      // 바닥 H132 의 노랑 쪽 — 볕 받는 밝은 풀색 수관
    understory: '#57b57c',
    rock: '#80957b',
    road: '#8fa591', water: '#52b2bd', cliff: '#7e9183', structure: '#899f8b',
    bands: ['#4fb29b', '#80957b', '#98af9b'],
  },
  namsan_edge: {
    name: '남산 자락 시가지 옅은 연두', tier: 'T1', sat: 0.66,
    desc: '산자락이 끝나고 시가지가 시작되는 칸(한남·청파·이태원·원효로1). 구 경계를 가장 많이 물고 있어 채도를 빼 이웃 구로 넘어갈 준비를 한다.',
    air: { hex: '#c0d4c2', den: 0.28 },
    // H150 — 초록 가족의 끝. 흙을 반쯤 되돌려(mixG 120) "산자락이 끝나고 시가지가
    // 시작된다"로 읽히게 한다. 구 경계를 가장 많이 물고 있는 칸이다(한남·청파·이태원).
    // 바닥 실렌더 H110 — 초록 가족의 끝. 흙을 반쯤 되돌려(mixG 120) 시가지로 읽히게 한다.
    ground: { tint: [170, 186, 162], mixG: 120, mixC: 70 },   // 화면 H100 S0.50 V0.68 · 연두(옅게 밝게)
    foliage: '#6d9660',      // 채도가 낮은 시가지 잎
    understory: '#6a9669',
    rock: '#8e9086',
    road: '#9ca298', water: '#6f8f95', cliff: '#888e84', structure: '#969c92',
    bands: ['#639677', '#8e9086', '#a4aaa0'],
  },
  ridge_moss: {
    name: '마른 황토 구릉', tier: 'T1', sat: 0.54,
    desc: '볕이 덜 드는 마른 흙 구릉. 석회 가족 안의 어둡고 탁한 칸 — 자동배정(서초 반포·방배 등)이 쓴다. ⚠ 남산 북사면은 이 칸이 아니라 `namsan_shade` 다.',
    air: { hex: '#c8dcc0', den: 0.3 },
    ground: { tint: [97, 101, 78], mixG: 130, mixC: 110 },   // 화면 H84 S0.55 V0.49 · 황록(마른 황토 구릉 — 어둡게)
    foliage: '#598a3c',
    understory: '#69a048',
    rock: '#8f9084',
    road: '#a89c84', water: '#7f8a84', cliff: '#948a76', structure: '#9c9484',
    bands: ['#8a9460', '#8f9084', '#a8a494'],
  },
  namsan_shade: {
    name: '남산 북사면 그늘 침엽록', tier: 'T1', sat: 0.68,
    desc: '중구인데 남산 북·동 사면(필동 263m · 장충 259m · 약수 163m). 볕이 안 드는 소나무 사면이라 초록 가족이고, 용산 능선과는 «명도»로 갈린다 — 여기가 더 어둡다.',
    // 응달의 공기는 «회색»이 아니라 «차가운 파랑»이다 — 실제 대기 원근이 그렇다. 회록(#90a29b)
    // 안개를 den 0.45 로 얹었더니 짙은 초록이 올리브 진창(H81 S0.21)으로 읽혔다. 채도 있는
    // 청회로 두면 어두워도 «그늘»로 읽힌다.
    air: { hex: '#86a8b8', den: 0.40 },
    // 실렌더 H151 · L*46. 용산 능선(H145 · L*55)과 색상 가족은 같고 명도가 9 낮다 —
    // 같은 산의 응달이라는 뜻이다. 어두움은 C(어두운 잔디)를 최대로 섞어 만든다.
    // ⚠ 이 칸을 밝히면 용산 능선과 구분이 사라진다(구 경계가 그 이음매다).
    // ⚠ **이 칸 때문에 감사의 「뒤집힘」 검사가 일부러 깨져 있다.** 구 안 최대(명동·광희동
    //   [limestone] ↔ 필동·장충동, ΔE 28.8)가 구 경계 최소(용산 능선 ↔ 이 칸, 15.3)보다 세다.
    //   그 검사는 「가장 센 이음매는 구 경계여야 한다」를 전제하는데, **남산은 구 경계를
    //   가로질러 이어진 하나의 산이다** — 산자락(도심↔산)이 구 선(산↔산)보다 센 것이 실제다.
    //   되돌리려면 이 칸을 지우고 DONG_BIOME 의 필동·장충·약수·다산·청구를 ridge_moss 로.
    ground: { tint: [54, 95, 90], mixG: 34, mixC: 215 },   // 화면 H156 S0.72 · 청록(그늘)
    foliage: '#356b4d',
    understory: '#257a70',
    rock: '#74807a',
    road: '#849086', water: '#5f7d7a', cliff: '#72807a', structure: '#7f8a84',
    bands: ['#3f6b50', '#74807a', '#8e9a94'],
  },

  limestone: {
    name: '석회 회백 도심', tier: 'T1', sat: 0.34,
    desc: '중구 도심 코어(명동·회현·소공…). 자연 0%. ★ 바닥은 노랑 쪽이지만 가로수까지 황토로 밀면 가을·고사목이 된다 — 바닥과 잎의 색상이 갈리는 유일한 바이옴.',
    air: { hex: '#e6dcc0', den: 0.3 },
    // 황록 방향(화면 #74954b) — 밝은 봄 잔디. tint 를 노랑(H40)으로 두면 sat 0.5 이상에서 겨자색 밀밭이 된다.
    // [주의] 바닥은 노랑 쪽이지만 **가로수까지 황토색으로 밀지 않는다** — 그러면 가을·고사목이
    // 된다. foliage 는 초록에 두고 채도만 뺀다(S 0.24). 바닥과 잎의 색상이 갈리는 유일한
    // 바이옴이고, 그래도 되는 이유는 전이가 **바닥에서만** 나기 때문이다.
    // ⚠ `sat` 는 이 바이옴의 «면적» 때문에 다른 T1 보다 낮다. 중구 도심 8개 동이 통째로
    //   이 칸이라, 회전이 켜지면 겨자색이 도심 전역을 덮는다 — 채도를 낮춰 「마른 회백」에
    //   묶어 둔다. 이 값을 올리려면 먼저 8개 동을 여러 종으로 쪼개라(§BAKED_BIOME_HUE).
    ground: { tint: [217, 219, 212], mixG: 165, mixC: 70 },   // 화면 H92 S0.36 V0.71 · 회백(밝고 바랜 포장) — 랩·게임 룩에서 약 15° 노랗게 나오므로 겨자(H62 아래)와 거리를 둔다
    foliage: '#7fae4a',      // 매연 먹은 가로수 H72 S0.24
    understory: '#a8b878',   // 보도블록 틈 마른 잡초
    rock: '#9a968c',         // 석회암·콘크리트
    road: '#b0a894', water: '#85908f', cliff: '#9c948a', structure: '#a8a498',
    bands: ['#94906f', '#9a968c', '#b0aca4'],
  },

  // ── 구(區) 테마 8종 — 「자연이 센 구」와 「시가지 구」를 갈라 장면전환을 만든다 ──────
  // 구 경계를 넘을 때 색 가족이 통째로 바뀌는 것이 이 카탈로그의 규약인데, 전환이 느껴지려면
  // 양쪽이 다 있어야 한다. 앞 여섯은 산이 지배하는 구(은평·종로·성북·강북·관악·서초),
  // 뒤 둘은 그 기준선이 되는 시가지 구(영등포·마포)다.
  // ⚠ 낮 채도를 올려 판타지를 만들지 않는다 — 무기는 안개(air.den)와 상판 재질(top) 둘이다.
  //   3D 가 읽는 색은 ground·foliage·air 셋뿐이고 road/water/cliff/structure 는 지도 전용이다.
  cloudsea: {
    name: '운해 침엽', tier: 'T2', sat: 0.5,
    desc: '구름이 능선에 걸려 안 걷힌다. 지표 바이옴 중 안개가 가장 짙은 축(den 0.58)이라 능선 너머가 안 보인다 — 「그 뒤에 뭐가 있나」를 안개가 만든다.',
    air: { hex: '#e8f2fa', den: 0.45 },
    ground: { tint: [115, 132, 144], mixG: 105, mixC: 85 },   // 화면 H166 S0.52 · 청록(구름바다)
    foliage: '#2f7a5f', understory: '#5fa09b', rock: '#8a9296',
    road: '#9aa4a6', water: '#4e7d90', cliff: '#7f8a8e', structure: '#93999a',
    bands: ['#4f7a70', '#8a9296', '#b6c0c2'],
  },
  inwang_white: {
    name: '인왕 백암', tier: 'T2', sat: 0.6,
    desc: '흰 화강암 위의 솔숲. 바위가 밝고 잎이 어두워 대비가 서고, 밤에는 그 바위가 달빛을 받아 은색으로 남는다.',
    air: { hex: '#e2e4e0', den: 0.30 },
    ground: { tint: [208, 203, 188], mixG: 58, mixC: 44 },     // H48 «밝게» — 중구 limestone 과 색상이 같은 난색대라
    //   갈라 주는 축이 색상이 아니라 명도다(L*58 -> L*80). 백암은 원래 눈부신 쪽이다.
    foliage: '#596b46', understory: '#7d8a6e', rock: '#c4c0b6',
    road: '#b0aca0', water: '#6b93a6', cliff: '#c8c4ba', structure: '#aca89e',
    bands: ['#8f9a80', '#b8b4aa', '#d8d4cc'],
  },
  emberfall: {
    name: '단풍 잿불', tier: 'T2', sat: 0.72,
    desc: '단풍 황혼보다 한 단 더 탄 색. 상판이 낙엽(litter)이라 색이 나무가 아니라 바닥에서 온다.',
    air: { hex: '#d99a70', den: 0.42 },
    top: { flat: 'litter', slope: 'litter' },
    ground: { tint: [140, 112, 92], mixG: 100, mixC: 75 },     // H24 탄 주홍
    foliage: '#b4512e', understory: '#a8553e', rock: '#8c8278',
    road: '#a89076', water: '#5f7f92', cliff: '#8a7f74', structure: '#9a8c7e',
    bands: ['#a8623a', '#8c8278', '#b4a894'],
  },
  snowridge: {
    name: '설령 능선', tier: 'T2', sat: 0.30,
    desc: '상판이 서리(frost)다. 카탈로그에서 채도가 가장 낮다 — 「높이 왔다」를 색이 아니라 탈색으로 말한다.',
    air: { hex: '#dcecfa', den: 0.32 },
    top: { flat: 'frost', slope: 'frost' },
    ground: { tint: [150, 152, 156], mixG: 80, mixC: 60 },     // H220 무채에 가까운 청
    foliage: '#2f5e4a', understory: '#a8c4d8', rock: '#a8acb0',
    road: '#b4b8bc', water: '#6f93a8', cliff: '#9aa0a6', structure: '#a4a8ac',
    bands: ['#8e9a96', '#a8acb0', '#dde2e6'],
  },
  gwanak_mist: {
    name: '관악 무령', tier: 'T2', sat: 0.48,
    desc: '지표에서 가장 어둡고 가장 짙다(den 0.62). 굴에 가까운 압박감을 땅 위에서 내는 유일한 바이옴 — 밝은 우면 죽림과 짝으로 읽힌다.',
    air: { hex: '#8fa2a8', den: 0.42 },
    ground: { tint: [90, 103, 118], mixG: 110, mixC: 90 },   // 화면 H170 S0.52 · 청록(안개)
    foliage: '#33564c', understory: '#5e7272', rock: '#767e7e',
    road: '#868e8c', water: '#3f6b78', cliff: '#6e7878', structure: '#7c8484',
    bands: ['#3f6252', '#767e7e', '#9aa4a2'],
  },
  bamboosea: {
    name: '우면 죽림', tier: 'T2', sat: 0.86,
    desc: '대나무 연두. 산 바이옴 여섯 중 유일하게 밝은 쪽이라, 어두운 관악·은평 사이에서 숨 쉴 자리가 된다.',
    air: { hex: '#d2e2c0', den: 0.34 },
    ground: { tint: [122, 136, 109], mixG: 115, mixC: 80 },   // 화면 H92 S0.66 · 연두(대숲)
    foliage: '#7cae3f', understory: '#94b85c', rock: '#8e9284',
    road: '#a6a88e', water: '#5a8f8a', cliff: '#88907e', structure: '#9aa08c',
    bands: ['#8ab04c', '#8e9284', '#b8bca6'],
  },
  hangang_sand: {
    name: '한강 은빛 모래벌', tier: 'T2', sat: 0.45,
    desc: '상판이 모래(sand)다. 모래톱 강변이 「좁은 물가」라면 이쪽은 넓고 마른 벌판 — 억새가 은빛으로 눕는다.',
    air: { hex: '#eae6d6', den: 0.30 },
    top: { flat: 'sand', slope: 'sand' },
    ground: { tint: [146, 142, 126], mixG: 90, mixC: 65 },     // H48 은모래
    foliage: '#a69c6a', understory: '#b0a388', rock: '#b4b0a4',
    road: '#bab4a2', water: '#6ea4b8', cliff: '#aca89a', structure: '#a8a496',
    bands: ['#a8ac8c', '#b4b0a4', '#dcd8c8'],
  },
  hanok_tile: {
    name: '기와 잿빛 마을', tier: 'T2', sat: 0.82,
    desc: '기와 회청이 건물색을 쥔다. 저녁 연기가 낮게 깔린 골목 — 평범하되 «어둡고 따뜻한» 쪽으로 평범하다.',
    // ⚠ 공기는 밝은 중성회(#c8c6bc)면 안 된다. 이웃 넷(은평 cloudsea·용산 reedmarsh·
    //   중구 limestone·영등포 hangang_sand)이 전부 밝은 회색대라 ΔE 0.9 까지 붙어
    //   「걸어 넘어도 공기가 안 바뀐다」로 감사에 걸렸다. 어둡고 따뜻한 쪽으로 벌린다.
    air: { hex: '#d2c2a6', den: 0.3 },
    ground: { tint: [116, 142, 132], mixG: 66, mixC: 52 },   // 화면 H144 S0.60 · 에메랄드 — 꽃숲(feygrove)·석회(limestone)·구름바다(cloudsea)와 경계 ΔE 19 이상
    foliage: '#489a5a', understory: '#5ab07b', rock: '#8a8880',
    road: '#9c9488', water: '#5e8498', cliff: '#7f7d76', structure: '#6e7278',
    bands: ['#6f8a60', '#8a8880', '#adaba2'],
  },

  // ── T3 공간 바이옴 5종 — 지표 동에 배정하지 않는다 ─────────────────────────
  // [왜 갈라 두나] 이 다섯은 **색이 아니라 공간**이다. 서울 지형 위에 보라를 칠하면
  //   「심연」이 아니라 보라색 서울이 나온다(/tech lab-district-biome 결론).
  //   그래서 SURFACE_KEYS 에서 빠지고, 굴·실내(underground_construction_sop §14)를
  //   팠을 때 그 공간의 팔레트로만 쓰인다. 도감에는 「공간」 등급으로 같이 보인다.
  crystal: {
    name: '자수정 결정', tier: 'T2', sat: 0.7,
    desc: '연보라 결정석 바닥에 자수정 결정이 박혀 빛난다(균열 발광 — 결정형). 공기는 보랏빛, 수관도 연보라. 청록은 운해·관악 안개가 쓰므로 보라로 갈랐다. 시안 crystal_abc B.',
    air: { hex: '#b8a4e8', den: 0.36 },
    ground: { tint: [124, 108, 150], mixG: 80, mixC: 120 },
    foliage: '#c4b0ee', understory: '#9a8ac4', rock: '#8f84b8',
    road: '#b4a8d0', water: '#9a7cf0', cliff: '#7c70a8', structure: '#b0a4d4',
    bands: ['#8f84b8', '#a698d0', '#c8bce8'],
  },
  abyss: {
    name: '심연 공허석', tier: 'T2', sat: 0.8,
    desc: '남흑 공허석 위에 청록 생물발광 점이 박혀 있다(균열 발광 — 결정형). 어둡되 발광 점으로 읽힌다 — 밝은 보라는 심연이 아니라 축제다. 시안 abyss_abc A.',
    air: { hex: '#3a2a6a', den: 0.5 },
    ground: { tint: [95, 86, 107], mixG: 90, mixC: 175 },
    foliage: '#1f5560', understory: '#22394a', rock: '#24243a',
    road: '#3a3a58', water: '#2ad8e8', cliff: '#1e1e32', structure: '#34345a',
    bands: ['#1b1b33', '#24243a', '#3a3a58'],
  },
  ruins: {
    name: '유적', tier: 'T3', sat: 0.6,
    desc: '고대 석로 종점. 색이 바래는 게 핵심 — 채도를 낮게 두어야 「오래된 것」으로 읽힌다. 진하면 새로 지은 세트장이 된다.',
    air: { hex: '#cec9b2', den: 0.35 },
    ground: { tint: [135, 134, 116], mixG: 150, mixC: 95 },
    foliage: '#8a9470', understory: '#94947a', rock: '#9c9884',
    road: '#a8a48c', water: '#7f8a7a', cliff: '#948f7d', structure: '#a49f8a',
    bands: ['#94947a', '#9c9884', '#b0ac98'],
  },
  sanctum: {
    name: '신전', tier: 'T3', sat: 0.7,
    desc: '실내 봉인 §13. 금빛이되 타면 안 된다 — 채도를 올리는 대신 명도를 올려 「빛이 드는 곳」으로 만든다.',
    air: { hex: '#e6d8b4', den: 0.4 },
    ground: { tint: [148, 141, 121], mixG: 170, mixC: 50 },
    foliage: '#c4a860', understory: '#b8a870', rock: '#b0a890',
    road: '#c4b894', water: '#8fa0a8', cliff: '#a89c80', structure: '#c4b898',
    bands: ['#b8a870', '#b0a890', '#d4c8a8'],
  },
  imperial: {
    name: '황제의 성', tier: 'T3', sat: 0.75,
    desc: '랜드마크 실내. 심홍 + 금. 이 카탈로그에서 유일하게 structure 가 주인공인 바이옴 — 자연이 아니라 사람이 지은 것이 색을 정한다.',
    air: { hex: '#c2a09c', den: 0.35 },
    ground: { tint: [128, 103, 107], mixG: 160, mixC: 85 },
    foliage: '#8a5f68', understory: '#7f5860', rock: '#8a7078',
    road: '#9c8088', water: '#6f5f75', cliff: '#7d646c', structure: '#b08a70',
    bands: ['#7f5860', '#8a7078', '#a8888f'],
  },
  infernal: {
    name: '지옥 진홍석', tier: 'T2', sat: 0.8,
    desc: '진홍 지옥석 바닥에 붉은 룬 균열이 빛난다(균열 발광). 공기는 짙은 진홍, 나무는 검붉게 그을린 진홍 수관. 화산(주황)과 색 가족이 갈린다. 시안 infernal_abc A.',
    air: { hex: '#a3202a', den: 0.48 },
    ground: { tint: [150, 70, 78], mixG: 120, mixC: 120 },
    foliage: '#6a1c26', understory: '#4a1218', rock: '#3a1418',
    road: '#5a2a26', water: '#ff3a3a', cliff: '#2c1014', structure: '#2a1a1c',
    bands: ['#5a1418', '#3a1418', '#7a2a2a'],
  },
  celestial: {
    name: '천상 대리석', tier: 'T2', sat: 0.5,
    desc: '흰 대리석 바닥에 금빛 결이 은은하게 빛난다(균열 발광 — 약하게). 공기는 밝은 금빛, 수관은 흰 꽃. 시안 celestial_abc A.',
    air: { hex: '#f6e8bc', den: 0.3 },
    ground: { tint: [170, 160, 120], mixG: 170, mixC: 30 },
    foliage: '#f4eee6', understory: '#e0c860', rock: '#e8e2d4',
    road: '#efe4c8', water: '#bfe4ff', cliff: '#d8d0bc', structure: '#f2ecdc',
    bands: ['#f1ebdc', '#e8e2d4', '#e0c860'],
  },
};

// ── 정체성 4축 — 「바이옴은 색이 아니라 «네 가지가 같이» 갈리는 것」 ────────────
// 레퍼런스(판타지 바이옴 도감 24종)가 한 칸을 읽히게 하는 것은 색이 아니라 넷이다:
//   form   무엇이 «서» 있나 — 실루엣(화산 분화구·거대 버섯·수정 첨탑·세계수)
//   ground 무엇이 «깔려» 있나 — 재질(용암·모래·얼음·뼈)
//   sky    공기·하늘·빛 — 대기(폭풍 구름·발광·안개)
//   life   무엇이 움직이고 무엇이 위협하나 — 생명·위험(불티·수은·역병·요정 불빛)
// 이 리포의 배선: 색(8층) 22종 전부 · 공기(air) 21종 · 그림(방향 벡터→마른 흙/축축한 그늘) ·
// 실루엣은 «나무 색 가족»까지(gen_block_trees BIOME_FAMILY) · 소리는 남산 BGM 하나.
// `form` 의 토큰은 도감·/tech 디오라마가 그리는 실루엣 어휘다(biome-identity.html).
export const BIOME_SIG = {
  temperate:     { form: ['broad', 'house'],            formText: '둥근 활엽수 + 기와집',      ground: '밟혀 마른 흙 위 잔디',        sky: '맑은 하늘 — 공기 없음(기준)',    life: '참새·닭·마을 사람' },
  sakura:        { form: ['cherry', 'lantern'],         formText: '분홍 수관 + 연등',           ground: '마른 흙길, 흩어진 꽃잎',      sky: '분홍 아지랑이',                  life: '꽃잎 낙하 · 나비' },
  conifer:       { form: ['pine', 'boulder'],           formText: '뾰족한 침엽수 + 바위',       ground: '솔잎 깔린 그늘 흙',           sky: '청회 산안개',                    life: '까마귀 · 멧돼지' },
  maple:         { form: ['maple', 'boulder'],          formText: '주홍 수관 + 바위',           ground: '낙엽 깔린 흙',                sky: '노을빛 공기',                    life: '낙엽 낙하 · 사슴' },
  volcanic:      { form: ['vent', 'dead', 'boulder'],   formText: '분기공 + 고사목',            ground: '현무암 · 용암 균열',          sky: '주황 유황 연무',                 life: '불티 · 용암 도마뱀' },
  snowfield:     { form: ['snowcap', 'pine', 'dead'],   formText: '눈 덮인 침엽수·고사목',      ground: '눈(상향면 마스크)',           sky: '희뿌연 설안개',                  life: '입김 · 눈보라' },
  arid:          { form: ['dead', 'boulder', 'cliff'],  formText: '고사목 + 마른 절벽',         ground: '갈라진 마른 흙',              sky: '흙먼지',                         life: '전갈 · 열기 아지랑이' },
  waterside:     { form: ['reed', 'broad'],             formText: '갈대 + 물가 활엽수',         ground: '젖은 진흙 · 자갈',            sky: '물안개',                         life: '개구리 · 반딧불' },
  jungle:        { form: ['broad', 'broad', 'reed'],    formText: '겹겹 활엽 수관',             ground: '짙은 부엽토',                 sky: '녹색 습기',                      life: '앵무 · 벌레 소리' },
  flowerfield:   { form: ['cherry', 'broad', 'lantern'], formText: '꽃나무 + 연등',             ground: '꽃잎 깔린 풀',                sky: '연분홍 빛',                      life: '나비 떼' },
  alpine:        { form: ['spire', 'boulder', 'snowcap'], formText: '암봉 + 눈 모자',           ground: '회청 암반',                   sky: '차고 얇은 공기',                 life: '독수리 · 바람' },
  duskviolet:    { form: ['dead', 'lantern', 'spire'],  formText: '고사목 + 등불 + 첨탑',       ground: '자보라 이끼',                 sky: '황혼 보라 안개',                 life: '반딧불 · 그림자' },
  windridge:     { form: ['reed', 'boulder', 'dead'],   formText: '억새 + 바랜 바위 + 마른 나무', ground: '밀짚빛 풀, 노출 화강암',     sky: '얇고 맑은 공기',                 life: '바람 소리 · 매' },
  sandbar:       { form: ['reed', 'broad', 'boulder'],  formText: '갈대 + 버드나무 + 강돌',     ground: '모래 · 물자국 띠',            sky: '따뜻한 강빛',                    life: '물새 · 사금' },
  icehouse:      { form: ['snowcap', 'pine', 'crystal'], formText: '서리 낀 침엽수 + 얼음 결정', ground: '서리 · 얼음 웅덩이',          sky: '차고 밝은 설안개',               life: '입김 · 얼음 결정' },
  cinderyard:    { form: ['dead', 'pillar', 'vent'],    formText: '고사목 + 철주 + 잉걸 구덩이', ground: '재 · 잉걸 점',                sky: '매캐한 회갈 연무',               life: '불티 · 쇳소리' },
  reedmarsh:     { form: ['reed', 'reed', 'dead'],      formText: '갈대 밭 + 물에 선 고사목',   ground: '청회 진흙 · 고인 물',          sky: '물안개',                         life: '개구리 · 반딧불' },
  fungalhollow:  { form: ['dead', 'lantern', 'reed'],   formText: '버섯 갓 + 포자 등불',        ground: '늪흙 · 포자 이끼',            sky: '보라 포자 안개',                 life: '반딧불 · 포자' },
  worldtree:     { form: ['broad', 'broad', 'house'],   formText: '거목 + 활엽수 + 정자',       ground: '금빛 초록 잔디',              sky: '금빛 볕',                        life: '새 떼 · 매미' },
  feygrove:      { form: ['broad', 'cherry', 'lantern'], formText: '비취 활엽수 + 꽃나무 + 등불', ground: '비취 잔디 위 꽃잎 융단',      sky: '연둣빛 반짝임',                  life: '나비 · 반딧불' },
  namsan_forest: { form: ['pine', 'pine', 'boulder'],   formText: '소나무 능선 + 화강암',       ground: '짙은 풀 · 이끼',              sky: '비취 안개',                      life: '솔바람 · 까치' },
  namsan_slope:  { form: ['broad', 'reed', 'house'],    formText: '활엽수 + 갈대 + 집',         ground: '밝은 풀, 물가 자갈',          sky: '연한 민트 안개',                 life: '물새' },
  namsan_edge:   { form: ['broad', 'house', 'lantern'], formText: '가로수 + 집 + 연등',         ground: '옅은 연두 잔디 · 포장',       sky: '엷은 공기',                      life: '골목 사람' },
  ridge_moss:    { form: ['dead', 'boulder', 'pine'],   formText: '마른 나무 + 바위',           ground: '마른 황토',                   sky: '황토 먼지',                      life: '메뚜기' },
  namsan_shade:  { form: ['pine', 'pine', 'cliff'],     formText: '소나무 + 응달 절벽',         ground: '축축한 그늘 풀',              sky: '차가운 청회 응달 공기',          life: '이끼 · 물방울' },
  limestone:     { form: ['house', 'house', 'pillar'],  formText: '집 + 집 + 석주',             ground: '마른 석회 포장',              sky: '회백 매연',                      life: '비둘기 · 시장 소리' },
  crystal:       { form: ['crystal', 'crystal', 'boulder'], formText: '자수정 첨탑',            ground: '자수정 박힌 결정석',          sky: '보랏빛 발광',                    life: '반짝임 · 울림' },
  abyss:         { form: ['spire', 'dead'],             formText: '검은 첨탑 + 고사목',         ground: '공허석 · 청록 발광 점',       sky: '짙은 남보라',                    life: '속삭임 · 발광 포자' },
  infernal:      { form: ['spire', 'pillar', 'dead'],   formText: '흑요석 가시 + 룬 석주 + 뼈 나무', ground: '진홍 지옥석 · 룬 균열',      sky: '짙은 진홍 연무',                 life: '불꽃 · 낮은 울음' },
  celestial:     { form: ['pillar', 'cherry', 'lantern'], formText: '흰 석주 + 흰 꽃나무 + 등불', ground: '흰 대리석 · 금빛 결',         sky: '금빛 볕',                        life: '빛 알갱이 · 종소리' },
  ruins:         { form: ['ruin', 'pillar', 'dead'],    formText: '무너진 벽 + 석주',           ground: '바랜 석판',                   sky: '먼지 낀 햇살',                   life: '거미줄' },
  sanctum:       { form: ['pillar', 'pillar', 'lantern'], formText: '금빛 석주 + 등불',         ground: '금빛 석판',                   sky: '빛기둥',                         life: '향 연기' },
  imperial:      { form: ['throne', 'pillar', 'lantern'], formText: '옥좌 + 석주 + 등불',       ground: '심홍 융단',                   sky: '금빛 실내광',                    life: '근위병' },
  emberfall:     { form: ['maple', 'dead', 'boulder'],  formText: '탄 단풍 + 고사목 + 바위',    ground: '두꺼운 낙엽',                 sky: '잿빛 노을',                      life: '불티 · 낙엽' },
  snowridge:     { form: ['snowcap', 'pine', 'spire'],  formText: '눈 모자 침엽 + 암봉',        ground: '서리 암반',                   sky: '탈색된 얇은 공기',               life: '바람 · 까마귀' },
  bamboosea:     { form: ['broad', 'broad', 'reed'],    formText: '연두 활엽 + 대숲 덤불',      ground: '밝은 연두 풀',                sky: '푸른 볕',                        life: '댓잎 소리 · 새' },
  cloudsea:      { form: ['pine', 'pine', 'spire'],     formText: '운해 위 침엽 + 암봉',        ground: '젖은 솔잎',                   sky: '능선에 걸린 구름',               life: '안개 · 까마귀' },
  gwanak_mist:   { form: ['pine', 'dead', 'boulder'],   formText: '짙은 침엽 + 고사목 + 바위',  ground: '어두운 부엽토',               sky: '짙은 안개',                      life: '올빼미 · 바람' },
  hangang_sand:  { form: ['reed', 'reed', 'boulder'],   formText: '은빛 억새 + 강돌',           ground: '마른 모래',                   sky: '넓고 마른 하늘',                 life: '물새 · 바람' },
  hanok_tile:    { form: ['broad', 'house', 'lantern'], formText: '활엽수 + 기와집 + 등',       ground: '잿빛 흙길',                   sky: '낮게 깔린 저녁 연기',            life: '골목 사람 · 개' },
  inwang_white:  { form: ['pine', 'boulder', 'boulder'], formText: '솔숲 + 흰 화강암',          ground: '흰 화강암 · 솔잎',            sky: '달빛 은색',                      life: '솔바람 · 까치' },
};

// ── 바이옴을 「행 번호」로 부르기 ────────────────────────────────
// 서브메시 병합(PlacedObjects) 뒤로는 바이옴 배율이 **CPU 가 아니라 셰이더**에서 곱해진다.
// 그러려면 배율표를 유니폼 배열로 올려야 하고, 배열엔 문자열 키를 못 넣는다 — 그래서
// 키에 번호를 매긴다. **0행은 「바이옴 없음」(백색)** 자리로 비워 둔다. 그래야 조회표가
// 아직 안 온 소품(biomeKeyFor 가 '' 를 주는 동안)이 0 을 쓰면 자연히 원색이 나온다 —
// 없음을 표현하려고 별도 분기를 두면 그 분기가 셰이더에도 한 번 더 생긴다.
// ⚠ **지표 바이옴만 행을 받는다**. T3(공간)는 굴·실내에서 쓰이고 지표 소품에
//   곱해질 일이 없는데, 행을 주면 유니폼 배열이 그만큼 커져 **서브메시 상한이 줄어든다**
//   (아래 BIOME_MUL_MAX 나눗셈). 즉 안 쓰는 바이옴이 쓰는 바이옴의 예산을 먹는다.
export const SURFACE_KEYS = Object.keys(BIOMES).filter((k) => BIOMES[k].tier !== 'T3');
export const CATALOG_KEYS = Object.keys(BIOMES);
export const BIOME_KEYS = SURFACE_KEYS;
export const BIOME_ROWS = BIOME_KEYS.length + 1;
const _rowOf = new Map(BIOME_KEYS.map((k, i) => [k, i + 1]));
export const biomeRowOf = (key) => _rowOf.get(key) || 0;
